/**
 * The valuation panels, as v1's Assumptions tab laid them out.
 *
 * v1 put three things on one tab: the assumptions, a DCF card beside a
 * Multiples card, and a summary table comparing the methods side by side. v2
 * had the assumptions (on Settings) and a blend breakdown, but never the
 * method cards or the comparison, so you could see WHAT the blend came to
 * without seeing what each method said on its own terms.
 *
 * These are v2's own components. v1 and v2 are separate apps that share stores
 * rather than components, so v1's AssumptionsView is untouched by
 * construction and its layout is reproduced here rather than imported.
 *
 * The comparison carries a fourth column v1 never had: the AI forecast's
 * Monte Carlo valuation. That is the point of putting them side by side - a
 * DCF built on the model's own forecast years and a DCF built on the
 * forecasters' view of the same company should be read against each other.
 */

import { formatMoney } from '../../../lib/currency';
import type { FinancialModelV2 } from '../../../types/financial-model-v2';

type Valuation = NonNullable<FinancialModelV2['computed']['valuation']>;

function money(value: number | null | undefined, currency: string): string {
  if (value == null) return '—';
  return formatMoney(value, currency);
}

/** A share price is the one figure here where the pence matter. */
function price(value: number | null | undefined, currency: string): string {
  if (value == null) return '—';
  return formatMoney(value, currency, { decimals: 2 });
}

function Line({
  label,
  value,
  strong = false,
  divider = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
  divider?: boolean;
}) {
  return (
    <div
      className={[
        'flex justify-between text-xs',
        divider ? 'border-t border-gray-100 pt-1.5' : '',
      ].join(' ')}
    >
      <span className={strong ? 'text-gray-700' : 'text-gray-500'}>{label}</span>
      <span
        className={
          strong ? 'font-medium tabular-nums text-blue-700' : 'tabular-nums text-gray-900'
        }
      >
        {value}
      </span>
    </div>
  );
}

/**
 * What each method produced, on its own terms.
 *
 * Both cards end on the implied share price, so the eye can run down one card
 * and across to the other at the same row.
 */
export function ValuationResultsV2({
  valuation,
  currency,
  terminalYear,
}: {
  valuation: Valuation;
  currency: string;
  terminalYear: number | string;
}) {
  const dcf = valuation.dcf;
  const multiples = valuation.multiples;
  if (!dcf && !multiples) return null;

  // Zero EBITDA does not make the multiples leg small, it makes it
  // meaningless, and the engine leaves it out of the blend. The card says so
  // rather than presenting a confident nil.
  const multiplesUsable = multiples != null && multiples.terminalEBITDA !== 0;

  return (
    <div className="mt-4">
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
        Valuation results
      </h4>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {dcf && (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-200 bg-gray-50 px-4 py-2">
              <h5 className="text-sm font-medium text-gray-900">DCF analysis</h5>
            </div>
            <div className="space-y-1.5 px-4 py-3">
              <Line
                label="Sum of PV of cash flows"
                value={money(dcf.sumOfPVCashFlows, currency)}
              />
              <Line label="Terminal value" value={money(dcf.terminalValue, currency)} />
              <Line
                label="PV of terminal value"
                value={money(dcf.pvTerminalValue, currency)}
              />
              <Line
                label="Enterprise value"
                value={money(dcf.enterpriseValue, currency)}
                divider
              />
              <Line label="Less: net debt" value={money(dcf.netDebt, currency)} />
              <Line
                label="Equity value"
                value={money(dcf.equityValue, currency)}
                strong
                divider
              />
              <Line
                label="Implied share price"
                value={price(dcf.impliedSharePrice, currency)}
                strong
              />
            </div>
          </div>
        )}

        {multiples && (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-200 bg-gray-50 px-4 py-2">
              <h5 className="text-sm font-medium text-gray-900">
                Multiples valuation
              </h5>
            </div>
            <div className="space-y-1.5 px-4 py-3">
              <Line
                label={`Terminal EBITDA (${terminalYear})`}
                value={money(multiples.terminalEBITDA, currency)}
              />
              <Line
                label="Exit multiple (EV/EBITDA)"
                value={`${multiples.exitMultiple.toFixed(1)}×`}
              />
              {multiplesUsable ? (
                <>
                  <Line
                    label="Enterprise value"
                    value={money(multiples.enterpriseValue, currency)}
                    divider
                  />
                  <Line
                    label="Less: net debt"
                    value={money(multiples.netDebt, currency)}
                  />
                  <Line
                    label="Equity value"
                    value={money(multiples.equityValue, currency)}
                    strong
                    divider
                  />
                  <Line
                    label="Implied share price"
                    value={price(multiples.impliedSharePrice, currency)}
                    strong
                  />
                </>
              ) : (
                <p className="border-t border-gray-100 pt-2 text-xs text-gray-500">
                  There is no EBITDA in the terminal year, so this method says
                  nothing and is left out of the blend rather than averaged in
                  as zero.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** One column of the comparison. `ev` of null means the method produced none. */
export interface MethodColumn {
  name: string;
  ev: number | null;
  equity: number | null;
  sharePrice: number | null;
  /** Shown under the enterprise value, e.g. a Monte Carlo range. */
  note?: string;
  /** The blend and the forecast are the figures to read; the legs support them. */
  emphasis?: boolean;
}

/**
 * Build the columns, including the AI forecast when one has been run.
 *
 * The forecast returns an enterprise value DISTRIBUTION and nothing else - no
 * net debt, no share count, because it values the company rather than the
 * model. Equity and share price are therefore derived here, from the same net
 * debt and share count the other methods used. That is the only way the
 * column is comparable with its neighbours, and the derivation is stated in
 * the panel rather than left for someone to infer.
 */
export function buildMethodColumns({
  valuation,
  forecastEV,
  netDebt,
  sharesOutstanding,
}: {
  valuation: Valuation;
  forecastEV: { median?: number; p25?: number; p75?: number } | null;
  netDebt: number | null;
  sharesOutstanding: number | null;
}): MethodColumn[] {
  const s = valuation.summary;
  const columns: MethodColumn[] = [];

  if (s.dcfEnterpriseValue != null) {
    columns.push({
      name: 'DCF',
      ev: s.dcfEnterpriseValue,
      equity: s.dcfEquityValue,
      sharePrice: s.dcfSharePrice,
    });
  }

  // A multiples leg with no terminal EBITDA is excluded upstream; showing a
  // column of nils would read as "the method says zero", which it does not.
  const multiplesUsable =
    valuation.multiples != null && valuation.multiples.terminalEBITDA !== 0;
  if (s.multiplesEnterpriseValue != null && multiplesUsable) {
    columns.push({
      name: 'Multiples',
      ev: s.multiplesEnterpriseValue,
      equity: s.multiplesEquityValue,
      sharePrice: s.multiplesSharePrice,
    });
  }

  columns.push({
    name: 'Blended',
    ev: s.blendedEnterpriseValue,
    equity: s.blendedEquityValue,
    sharePrice: s.blendedSharePrice,
    emphasis: true,
  });

  const median = forecastEV?.median;
  if (median != null) {
    const equity = netDebt == null ? null : median - netDebt;
    const sharePrice =
      equity == null || !sharesOutstanding ? null : equity / sharesOutstanding;
    const low = forecastEV?.p25;
    const high = forecastEV?.p75;
    columns.push({
      name: 'AI forecast',
      ev: median,
      equity,
      sharePrice,
      note:
        low != null && high != null
          ? `median of ${Math.round(low).toLocaleString()}–${Math.round(high).toLocaleString()}`
          : 'median',
      emphasis: true,
    });
  }

  return columns;
}

/**
 * Every method side by side, one column each.
 *
 * A single blended figure hides disagreement, and disagreement between two
 * methods is the most informative thing on the page: it is the difference
 * between "this company is worth about X" and "nobody agrees what this is
 * worth".
 */
export function ValuationSummaryV2({
  columns,
  currency,
  derivedFromNetDebt,
}: {
  columns: MethodColumn[];
  currency: string;
  derivedFromNetDebt: boolean;
}) {
  if (columns.length === 0) return null;

  const rows: { label: string; read: (c: MethodColumn) => string }[] = [
    { label: 'Enterprise value', read: (c) => money(c.ev, currency) },
    { label: 'Equity value', read: (c) => money(c.equity, currency) },
    { label: 'Implied share price', read: (c) => price(c.sharePrice, currency) },
  ];

  return (
    <div className="mt-4">
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
        Valuation summary
      </h4>
      <div className="rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 p-4">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-blue-200">
                <th scope="col" className="px-3 py-2 text-left font-medium text-gray-600">
                  Metric
                </th>
                {columns.map((column) => (
                  <th
                    key={column.name}
                    scope="col"
                    className={[
                      'px-3 py-2 text-right font-medium',
                      column.emphasis ? 'text-blue-700' : 'text-gray-600',
                    ].join(' ')}
                  >
                    {column.name}
                    {column.note && (
                      <span className="block font-normal text-gray-500">
                        {column.note}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr
                  key={row.label}
                  className={index < rows.length - 1 ? 'border-b border-blue-100' : ''}
                >
                  <td className="px-3 py-2 text-gray-600">{row.label}</td>
                  {columns.map((column) => (
                    <td
                      key={column.name}
                      className={[
                        'px-3 py-2 text-right tabular-nums',
                        column.emphasis
                          ? 'font-semibold text-blue-700'
                          : 'text-gray-900',
                      ].join(' ')}
                    >
                      {row.read(column)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {derivedFromNetDebt && (
          <p className="mt-3 text-xs text-gray-600">
            The AI forecast values the company, not this model, so it returns an
            enterprise value only. Its equity value and share price use the same
            net debt and share count as the other methods, so the columns are
            comparable.
          </p>
        )}
      </div>
    </div>
  );
}
